import { FundTradingEntityAssignment, SpreadsheetMapping, ValidationIssue } from 'api'
import { Locale, Translation } from 'locale'
import { isEmpty } from 'lodash'
import { Nullable } from 'utils'
import { gatherIssues, parseNumber, parseString, TypedSpreadsheetParseResult } from 'view/shared/upload/model'

export interface PendingTradingEntityAssignment {
  tradingEntityId: Nullable<string>
  notional: Nullable<number>
  startConsideration: Nullable<number>
  buySideEntity: Nullable<string>
  initiatorBook: Nullable<string>
  initiatorStrategy: Nullable<string>
  selected: boolean
}

export const pendingTradingToFundTradingEntityAssignmentMapper = (
  pending: PendingTradingEntityAssignment
): Partial<FundTradingEntityAssignment> => {
  return {
    tradingEntityId: pending.tradingEntityId ?? undefined,
    startConsideration: pending.startConsideration,
    notional: pending.notional,
    initiatorBook: pending.initiatorBook,
    initiatorStrategy: pending.initiatorStrategy,
    selected: pending.selected
  }
}

export enum PendingTradingEntityAssignmentFields {
  NOTIONAL = 'NOTIONAL',
  START_CONSIDERATION = 'START_CONSIDERATION',
  BUY_SIDE_ENTITY = 'BUY_SIDE_ENTITY',
  BOOK = 'BOOK',
  STRATEGY = 'STRATEGY'
}

export interface TradingEntityAssignmentSpreadsheetParseResult extends TypedSpreadsheetParseResult<PendingTradingEntityAssignment> {}

interface TradingEntityAssignmenSpreadsheetParseRowResult {
  parseIssues: ValidationIssue[]
  item: PendingTradingEntityAssignment
}

interface TradingEntityAssignmentParseParams {
  rowData: any[]
  filename: string
  spreadsheetMapping: SpreadsheetMapping
}

export const parseTradingEntityAssignmentSpreadsheet = ({
  rowData: rows,
  filename,
  spreadsheetMapping
}: TradingEntityAssignmentParseParams): TradingEntityAssignmentSpreadsheetParseResult =>
  rows.reduce<TradingEntityAssignmentSpreadsheetParseResult>(
    (r, row, idx) => {
      const { parseIssues, item } = parseRow(row, idx + 1, spreadsheetMapping)
      return {
        ...r,
        issues: [...r.issues, ...parseIssues],
        parsedItems: isEmpty(parseIssues) ? [...r.parsedItems, item] : r.parsedItems
      }
    },
    { filename, rows, issues: [], parsedItems: [] }
  )

const parseRow = (row: any, rowNum: number, spreadsheetMapping: SpreadsheetMapping): TradingEntityAssignmenSpreadsheetParseRowResult => {
  const buySideEntity = parseString(
    row,
    rowNum,
    spreadsheetMapping.shortName,
    Locale.translate(Translation.TC_TRADING_ENTITY_SHORT_NAME),
    true
  )
  const notional = parseNumber(row, rowNum, spreadsheetMapping.notional, Locale.translate(Translation.TC_NOTIONAL_SHORT), true)
  const startConsideration = parseNumber(
    row,
    rowNum,
    spreadsheetMapping.startConsideration,
    Locale.translate(Translation.TC_START_CONSIDERATION),
    true
  )
  const initiatorBook = parseString(row, rowNum, spreadsheetMapping.book, Locale.translate(Translation.TC_BOOK), false)
  const initiatorStrategy = parseString(row, rowNum, spreadsheetMapping.strategy, Locale.translate(Translation.TC_STRATEGY), false)

  return {
    parseIssues: gatherIssues(buySideEntity, notional, startConsideration, initiatorBook, initiatorStrategy),
    item: {
      tradingEntityId: null,
      selected: false,
      buySideEntity: buySideEntity.isValue ? buySideEntity.value : null,
      notional: notional.isValue ? notional.value : null,
      startConsideration: startConsideration.isValue ? startConsideration.value : null,
      initiatorBook: initiatorBook?.isValue ? initiatorBook?.value : null,
      initiatorStrategy: initiatorStrategy?.isValue ? initiatorStrategy?.value : null
    }
  }
}
